// src/pages/MonitorControl.tsx
import React, { useRef, useEffect, useState } from "react";
import {
  Card,
  Button,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Tabs,
  Switch,
  Dropdown,
  message,
  Space,
} from "antd";
import {
  ReloadOutlined,
  PlayCircleOutlined,
  BugOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import type { ColumnsType, TableProps } from "antd/es/table";
import { useDataProvider } from "@refinedev/core";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";

const MONITOR_SCHEMA = "monitor";

/**
 * =========================================================
 * UI & LAYOUT CONFIGURATION
 * =========================================================
 */
const UI = {
  PAGE_MIN_WIDTH_PX: 520,
  PAGE_PADDING: "16px 8px",
};

const TABLE_SCROLL_WIDTH: number | "max-content" = 1200;
const DISABLE_CELL_TEXT_WRAP = true;
const FORCE_TABS_SINGLE_ROW = true;
const HIDE_SCROLLBAR_UI = true;
const TABLE_SIZE: NonNullable<TableProps<any>["size"]> = "middle";
const ENABLE_RESIZABLE_COLUMNS = true;
const LS_DEFAULT_TAB_KEY = "monitorControl.defaultTabKey";

/* -------------------- Types -------------------- */
type MonitorRow = {
  id: number;
  kind: "url" | "fqdn" | "ip";
  target: string;
  module: string;
  period: string;
  retention: string;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: boolean | null;
  created_at: string;
  updated_at: string;
};

type LatestRow = {
  id: number;
  target: string;
  module: string;
  last_time: string | null;
  success: boolean | null;
  http_status_code: number | null;
  probe_duration_seconds: number | null;
  resolve_ip: string | null;
  tls_cert_expiry_days: number | null;
};

type FailureRow = {
  time: string;
  monitor_id: number;
  module: string;
  target: string;
  error_message: string | null;
  url: string | null;
  body_head: string | null;
};

type AlertPolicyRow = {
  id: number;
  name: string;
  condition: string;
  threshold_num: number;
  eval_window: string | null;
  severity: string;
  enabled: boolean;
  monitor_id: number | null;
  target_like: string | null;
  module: string | null;
  for_minutes: number | null;
};

type AlertRow = {
  id: number;
  policy_id: number;
  monitor_id: number;
  state: "firing" | "resolved" | string;
  started_at: string;
  ended_at: string | null;
  last_eval_at: string;
  reason: string | null;
  severity: string;
};

type SilenceRow = {
  id: number;
  monitor_id: number | null;
  target_like: string | null;
  module: string | null;
  starts_at: string;
  ends_at: string;
  reason: string | null;
  created_at: string;
};

/* -------------------- Resizable Components -------------------- */
type ResizableTitleProps = React.HTMLAttributes<any> & {
  width?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
};

const ResizableTitle: React.FC<ResizableTitleProps> = (props) => {
  const { onResize, width, style, ...rest } = props;
  if (!width) return <th {...rest} />;

  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize!}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        {...rest}
        style={{
          ...style,
          whiteSpace: DISABLE_CELL_TEXT_WRAP ? "nowrap" : "normal",
        }}
      />
    </Resizable>
  );
};

function useResizableColumns<T extends { key?: React.Key }>(
  base: ColumnsType<T>,
  initial: number[],
): { columns: ColumnsType<T>; components?: TableProps<T>["components"] } {
  if (!ENABLE_RESIZABLE_COLUMNS) {
    return { columns: base, components: undefined };
  }

  const [cols, setCols] = React.useState(
    base.map((c, i) => ({
      ...c,
      width: initial[i] ?? (c.width as number) ?? 120,
    })),
  );

  const handleResize =
    (index: number) =>
    (_: any, { size }: { size: { width: number } }) => {
      setCols((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], width: Math.max(size.width, 80) };
        return next;
      });
    };

  const columns = cols.map((col, index) => ({
    ...col,
    onHeaderCell: (column: any) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  const components: TableProps<T>["components"] = {
    header: { cell: ResizableTitle },
  };

  return { columns, components };
}

/* -------------------- Main Component -------------------- */
const MonitorControl: React.FC = () => {
  const getDp = useDataProvider();
  const dp = getDp();

  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const readDefaultTab = () => {
    try {
      return localStorage.getItem(LS_DEFAULT_TAB_KEY) || "status";
    } catch {
      return "status";
    }
  };
  const [defaultTabKey, setDefaultTabKey] =
    React.useState<string>(readDefaultTab);
  const [tab, setTab] = React.useState<string>(readDefaultTab);
  const [toolbarAction, setToolbarAction] = React.useState<string | undefined>(
    undefined,
  );

  // Data
  const [monitors, setMonitors] = React.useState<MonitorRow[]>([]);
  const [latest, setLatest] = React.useState<LatestRow[]>([]);
  const [failures, setFailures] = React.useState<FailureRow[]>([]);
  const [policies, setPolicies] = React.useState<AlertPolicyRow[]>([]);
  const [alerts, setAlerts] = React.useState<AlertRow[]>([]);
  const [silences, setSilences] = React.useState<SilenceRow[]>([]);

  // Drawer (Slider) State
  const [monitorModalOpen, setMonitorModalOpen] = React.useState(false);
  const [monitorEditing, setMonitorEditing] =
    React.useState<Partial<MonitorRow> | null>(null);
  const [monitorForm] = Form.useForm();

  // --- FOCUS MANAGEMENT ---
  const topDrawerRef = useRef<HTMLDivElement>(null); // For focus INSIDE drawer
  const lastFocusedElement = useRef<HTMLElement | null>(null); // For restoring focus OUTSIDE drawer
  const fallbackFocusRef = useRef<any>(null); // Search box as fallback

  // Capture focus before opening
  const captureFocus = () => {
    if (document.activeElement instanceof HTMLElement) {
      lastFocusedElement.current = document.activeElement;
    }
  };

  // Restore focus on close
  const handleDrawerClose = () => {
    setMonitorModalOpen(false);
    setMonitorEditing(null);

    // Attempt restore
    setTimeout(() => {
      if (
        lastFocusedElement.current &&
        document.body.contains(lastFocusedElement.current)
      ) {
        lastFocusedElement.current.focus();
      } else {
        // Fallback to top-left (search input)
        fallbackFocusRef.current?.focus();
      }
    }, 50);
  };

  // Snap focus to top of drawer when opened
  useEffect(() => {
    if (monitorModalOpen) {
      setTimeout(() => {
        topDrawerRef.current?.focus();
      }, 50);
    }
  }, [monitorModalOpen]);

  // Load Data
  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [monRes, latestRes, failRes, polRes, alertRes, silRes] =
        await Promise.all([
          dp.getList<MonitorRow>({
            resource: "monitors",
            pagination: { current: 1, pageSize: 500 },
            sorters: [{ field: "id", order: "asc" }],
            meta: { schema: MONITOR_SCHEMA },
          }),
          dp.getList<LatestRow>({
            resource: "v_monitor_latest",
            pagination: { current: 1, pageSize: 500 },
            sorters: [{ field: "id", order: "asc" }],
            meta: { schema: MONITOR_SCHEMA },
          }),
          dp.getList<FailureRow>({
            resource: "v_monitor_failures_24h",
            pagination: { current: 1, pageSize: 500 },
            meta: { schema: MONITOR_SCHEMA },
          }),
          dp.getList<AlertPolicyRow>({
            resource: "alert_policies",
            pagination: { current: 1, pageSize: 500 },
            sorters: [{ field: "id", order: "asc" }],
            meta: { schema: MONITOR_SCHEMA },
          }),
          dp.getList<AlertRow>({
            resource: "alerts",
            pagination: { current: 1, pageSize: 500 },
            sorters: [{ field: "last_eval_at", order: "desc" }],
            meta: { schema: MONITOR_SCHEMA },
          }),
          dp.getList<SilenceRow>({
            resource: "silences",
            pagination: { current: 1, pageSize: 500 },
            sorters: [{ field: "starts_at", order: "desc" }],
            meta: { schema: MONITOR_SCHEMA },
          }),
        ]);

      setMonitors(monRes.data);
      setLatest(latestRes.data);
      setFailures(failRes.data);
      setPolicies(polRes.data);
      setAlerts(alertRes.data);
      setSilences(silRes.data);
    } catch (err) {
      console.error(err);
      message.error("Failed to load monitor data.");
    } finally {
      setLoading(false);
    }
  }, [dp]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Actions
  const apiBase = dp.getApiUrl().replace(/\/$/, "");
  const runRpc = async (name: string, body: Record<string, any>) => {
    return dp.custom({
      url: `${apiBase}/rpc/${name}`,
      method: "POST",
      meta: { schema: MONITOR_SCHEMA },
      headers: { "Content-Type": "application/json" },
      data: body,
    });
  };

  const openNewMonitor = () => {
    captureFocus();
    setMonitorEditing(null);
    monitorForm.resetFields();
    monitorForm.setFieldsValue({
      kind: "url",
      module: "http_2xx",
      period: "00:01:00",
      retention: "30 days",
      enabled: true,
    });
    setMonitorModalOpen(true);
  };
  const openEditMonitor = (row: MonitorRow) => {
    captureFocus();
    setMonitorEditing(row);
    monitorForm.setFieldsValue(row);
    setMonitorModalOpen(true);
  };
  const saveMonitor = async () => {
    try {
      const values = await monitorForm.validateFields();
      if (monitorEditing?.id) {
        await dp.update({
          resource: "monitors",
          id: monitorEditing.id,
          variables: values,
          meta: { schema: MONITOR_SCHEMA },
        });
      } else {
        await dp.create({
          resource: "monitors",
          variables: values,
          meta: { schema: MONITOR_SCHEMA },
        });
      }
      handleDrawerClose();
      loadAll();
    } catch (err) {
      console.error(err);
      message.error("Failed to save monitor.");
    }
  };
  const deleteMonitor = async (row: MonitorRow) => {
    try {
      await dp.deleteOne({
        resource: "monitors",
        id: row.id,
        meta: { schema: MONITOR_SCHEMA },
      });
      loadAll();
    } catch {
      message.error("Failed.");
    }
  };
  const runOnce = async (row: MonitorRow) => {
    try {
      await runRpc("run_monitor_once", { p_monitor_id: row.id });
      setTimeout(loadAll, 400);
    } catch {
      message.error("Failed.");
    }
  };
  const debugProbe = async (row: MonitorRow) => {
    try {
      const res = await runRpc("debug_probe", { p_monitor_id: row.id });
      console.log(res.data);
      message.success("Output logged.");
    } catch {
      message.error("Failed.");
    }
  };
  const setAndPersistDefaultTab = (key: string) => {
    setDefaultTabKey(key);
    try {
      localStorage.setItem(LS_DEFAULT_TAB_KEY, key);
    } catch {}
  };
  const onToolbarAction = async (val: string) => {
    setToolbarAction(val);
    if (!val) return;
    try {
      if (val === "new_monitor") openNewMonitor();
      if (val === "reload") await loadAll();
      if (val === "eval_alerts") {
        await runRpc("evaluate_alerts", {});
        setTimeout(loadAll, 300);
      }
      if (val === "run_due") {
        await runRpc("run_due_monitors", {});
        setTimeout(loadAll, 400);
      }
    } finally {
      setTimeout(() => setToolbarAction(undefined), 100);
    }
  };

  // Filter
  const filteredMonitors = monitors.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      m.target.toLowerCase().includes(s) ||
      m.module.toLowerCase().includes(s) ||
      String(m.id).includes(s)
    );
  });

  const getScroll = () => ({ x: TABLE_SCROLL_WIDTH });

  // --- Column Definitions ---
  const baseMonitorCols: ColumnsType<MonitorRow> = [
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "left",
      render: (_, row) => (
        <Dropdown
          menu={{
            items: [
              { key: "run", icon: <PlayCircleOutlined />, label: "Run" },
              { key: "debug", icon: <BugOutlined />, label: "Debug" },
              { type: "divider" },
              { key: "edit", label: "Edit" },
              { key: "del", danger: true, label: "Delete" },
            ],
            onClick: ({ key }) => {
              if (key === "run") runOnce(row);
              if (key === "debug") debugProbe(row);
              if (key === "edit") openEditMonitor(row); // Use this to open drawer
              if (key === "del") deleteMonitor(row);
            },
          }}
          trigger={["click"]}
        >
          <Button
            size="small"
            type="text"
            icon={<EllipsisOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id, width: 80 },
    {
      title: "Kind",
      dataIndex: "kind",
      sorter: (a, b) => a.kind.localeCompare(b.kind),
      width: 100,
    },
    {
      title: "Target",
      dataIndex: "target",
      sorter: (a, b) => a.target.localeCompare(b.target),
      width: 350,
    },
    {
      title: "Module",
      dataIndex: "module",
      sorter: (a, b) => a.module.localeCompare(b.module),
      width: 160,
    },
    {
      title: "Period",
      dataIndex: "period",
      sorter: (a, b) => a.period.localeCompare(b.period),
      width: 120,
    },
    {
      title: "Retention",
      dataIndex: "retention",
      sorter: (a, b) => a.retention.localeCompare(b.retention),
      width: 140,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      sorter: (a, b) => Number(a.enabled) - Number(b.enabled),
      width: 110,
      render: (v) =>
        v ? <Tag color="green">enabled</Tag> : <Tag color="red">disabled</Tag>,
    },
    {
      title: "Last Run",
      dataIndex: "last_run_at",
      sorter: (a, b) =>
        (a.last_run_at || "").localeCompare(b.last_run_at || ""),
      width: 180,
    },
    {
      title: "Status",
      dataIndex: "last_status",
      sorter: (a, b) => Number(!!a.last_status) - Number(!!b.last_status),
      width: 80,
      render: (v) => (v == null ? "-" : v ? "✅" : "❌"),
    },
  ];
  const { columns: monitorCols, components: monitorComps } =
    useResizableColumns(
      baseMonitorCols,
      [50, 80, 100, 350, 160, 120, 140, 110, 180, 80],
    );

  const baseLatestCols: ColumnsType<LatestRow> = [
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id, width: 80 },
    {
      title: "Target",
      dataIndex: "target",
      sorter: (a, b) => a.target.localeCompare(b.target),
      width: 350,
    },
    {
      title: "Module",
      dataIndex: "module",
      sorter: (a, b) => a.module.localeCompare(b.module),
      width: 160,
    },
    {
      title: "Last Time",
      dataIndex: "last_time",
      sorter: (a, b) => (a.last_time || "").localeCompare(b.last_time || ""),
      width: 180,
      render: (v) => v ?? "-",
    },
    {
      title: "OK?",
      dataIndex: "success",
      sorter: (a, b) => Number(!!a.success) - Number(!!b.success),
      width: 80,
      render: (v) => (v == null ? "-" : v ? "✅" : "❌"),
    },
    {
      title: "HTTP",
      dataIndex: "http_status_code",
      sorter: (a, b) => (a.http_status_code ?? 0) - (b.http_status_code ?? 0),
      width: 90,
      render: (v) => v ?? "-",
    },
    {
      title: "Dur (s)",
      dataIndex: "probe_duration_seconds",
      sorter: (a, b) =>
        (a.probe_duration_seconds ?? 0) - (b.probe_duration_seconds ?? 0),
      width: 100,
      render: (v) => v ?? "-",
    },
    {
      title: "IP",
      dataIndex: "resolve_ip",
      sorter: (a, b) => (a.resolve_ip || "").localeCompare(b.resolve_ip || ""),
      width: 160,
      render: (v) => v ?? "-",
    },
    {
      title: "TLS Exp",
      dataIndex: "tls_cert_expiry_days",
      sorter: (a, b) =>
        (a.tls_cert_expiry_days ?? 0) - (b.tls_cert_expiry_days ?? 0),
      width: 120,
      render: (v) => v ?? "-",
    },
  ];
  const { columns: latestCols, components: latestComps } = useResizableColumns(
    baseLatestCols,
    [80, 350, 160, 180, 80, 90, 100, 160, 120],
  );

  const baseFailureCols: ColumnsType<FailureRow> = [
    {
      title: "Time",
      dataIndex: "time",
      sorter: (a, b) => a.time.localeCompare(b.time),
      width: 200,
    },
    {
      title: "Monitor",
      dataIndex: "monitor_id",
      sorter: (a, b) => a.monitor_id - b.monitor_id,
      width: 100,
    },
    {
      title: "Target",
      dataIndex: "target",
      sorter: (a, b) => a.target.localeCompare(b.target),
      width: 350,
    },
    {
      title: "Error",
      dataIndex: "error_message",
      sorter: (a, b) =>
        (a.error_message || "").localeCompare(b.error_message || ""),
      width: 600,
      render: (v) => v ?? "-",
    },
    {
      title: "URL",
      dataIndex: "url",
      sorter: (a, b) => (a.url || "").localeCompare(b.url || ""),
      width: 400,
      render: (v) => v ?? "-",
    },
    {
      title: "Body (head)",
      dataIndex: "body_head",
      sorter: (a, b) => (a.body_head || "").localeCompare(b.body_head || ""),
      width: 600,
      render: (v) => v ?? "-",
    },
  ];
  const { columns: failureCols, components: failureComps } =
    useResizableColumns(baseFailureCols, [200, 100, 350, 600, 400, 600]);

  const baseAlertCols: ColumnsType<AlertRow> = [
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id, width: 80 },
    {
      title: "Monitor",
      dataIndex: "monitor_id",
      sorter: (a, b) => a.monitor_id - b.monitor_id,
      width: 100,
    },
    {
      title: "State",
      dataIndex: "state",
      sorter: (a, b) => a.state.localeCompare(b.state),
      width: 120,
      render: (s) =>
        s === "firing" ? <Tag color="error">firing</Tag> : <Tag>resolved</Tag>,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      sorter: (a, b) => a.severity.localeCompare(b.severity),
      width: 120,
    },
    {
      title: "Started",
      dataIndex: "started_at",
      sorter: (a, b) => a.started_at.localeCompare(b.started_at),
      width: 180,
    },
    {
      title: "Last Eval",
      dataIndex: "last_eval_at",
      sorter: (a, b) => a.last_eval_at.localeCompare(b.last_eval_at),
      width: 180,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      sorter: (a, b) => (a.reason || "").localeCompare(b.reason || ""),
      width: 600,
      render: (v) => v ?? "-",
    },
  ];
  const { columns: alertCols, components: alertComps } = useResizableColumns(
    baseAlertCols,
    [80, 100, 120, 120, 180, 180, 600],
  );

  const basePolicyCols: ColumnsType<AlertPolicyRow> = [
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id, width: 80 },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 300,
    },
    {
      title: "Condition",
      dataIndex: "condition",
      sorter: (a, b) => a.condition.localeCompare(b.condition),
      width: 220,
    },
    {
      title: "Threshold",
      dataIndex: "threshold_num",
      sorter: (a, b) => a.threshold_num - b.threshold_num,
      width: 120,
    },
    {
      title: "Window",
      dataIndex: "eval_window",
      sorter: (a, b) =>
        (a.eval_window || "").localeCompare(b.eval_window || ""),
      width: 150,
      render: (v) => v ?? "-",
    },
    {
      title: "Severity",
      dataIndex: "severity",
      sorter: (a, b) => a.severity.localeCompare(b.severity),
      width: 120,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      sorter: (a, b) => Number(a.enabled) - Number(b.enabled),
      width: 100,
      render: (v) =>
        v ? <Tag color="green">yes</Tag> : <Tag color="red">no</Tag>,
    },
  ];
  const { columns: policyCols, components: policyComps } = useResizableColumns(
    basePolicyCols,
    [80, 300, 220, 120, 150, 120, 100],
  );

  const baseSilenceCols: ColumnsType<SilenceRow> = [
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id, width: 80 },
    {
      title: "Scope",
      key: "scope",
      width: 300,
      render: (r) =>
        r.monitor_id
          ? `monitor:${r.monitor_id}`
          : r.target_like
            ? `like:${r.target_like}`
            : "-",
    },
    {
      title: "Starts",
      dataIndex: "starts_at",
      sorter: (a, b) => a.starts_at.localeCompare(b.starts_at),
      width: 180,
    },
    {
      title: "Ends",
      dataIndex: "ends_at",
      sorter: (a, b) => a.ends_at.localeCompare(b.ends_at),
      width: 180,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      sorter: (a, b) => (a.reason || "").localeCompare(b.reason || ""),
      width: 600,
      render: (v) => v ?? "-",
    },
  ];
  const { columns: silenceCols, components: silenceComps } =
    useResizableColumns(baseSilenceCols, [80, 300, 180, 180, 600]);

  return (
    <div style={{ padding: UI.PAGE_PADDING, minWidth: UI.PAGE_MIN_WIDTH_PX }}>
      <style>
        {`
          /* --- GLOBAL STYLES FOR MONITOR CONTROL --- */
          
          ${
            HIDE_SCROLLBAR_UI
              ? `
            .monitor-table .ant-table-body,
            .monitor-tabs .ant-tabs-nav-wrap {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            .monitor-table .ant-table-body::-webkit-scrollbar,
            .monitor-tabs .ant-tabs-nav-wrap::-webkit-scrollbar {
              display: none;
            }
          `
              : ""
          }

          ${
            FORCE_TABS_SINGLE_ROW
              ? `
            .monitor-tabs .ant-tabs-nav-wrap { overflow-x: auto; overflow-y: hidden; }
            .monitor-tabs .ant-tabs-nav-list { flex-wrap: nowrap; white-space: nowrap; }
          `
              : ""
          }

          .monitor-table .ant-table-cell {
            ${DISABLE_CELL_TEXT_WRAP ? "white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" : ""}
            padding: 10px 12px !important;
          }

          .monitor-toolbar {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .monitor-toolbar-top {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            align-items: center;
          }
          .monitor-toolbar-action {
            width: 100%;
          }

          @media (min-width: 768px) {
            .monitor-toolbar { flex-direction: row; flex-wrap: nowrap; }
            .monitor-toolbar-top { flex: 1; flex-wrap: nowrap; }
            .monitor-toolbar-action { width: 300px; }
          }
        `}
      </style>

      {/* --- TOOLBAR --- */}
      <Card bodyStyle={{ padding: 12 }}>
        <div className="monitor-toolbar">
          <div className="monitor-toolbar-top">
            <Input.Search
              ref={fallbackFocusRef} // Top-left fallback focus
              placeholder="Search target/module/id..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ minWidth: "250px", flex: 1 }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <span style={{ opacity: 0.6, fontSize: "12px" }}>Default:</span>
              <Select
                value={defaultTabKey}
                style={{ width: 130 }}
                options={[
                  { value: "status", label: "Status" },
                  { value: "latest", label: "Latest" },
                  { value: "failures", label: "Failures" },
                  { value: "alerts", label: "Alerts" },
                  { value: "policies", label: "Policies" },
                  { value: "silences", label: "Silences" },
                ]}
                onChange={setAndPersistDefaultTab}
              />
            </div>
          </div>
          <div className="monitor-toolbar-action">
            <Select
              value={toolbarAction}
              placeholder="Select Action..."
              style={{ width: "100%" }}
              allowClear
              onClear={() => setToolbarAction(undefined)}
              onChange={(v) => onToolbarAction(v)}
              size="large"
              options={[
                { value: "new_monitor", label: "+ New Monitor" },
                { value: "reload", label: "Reload Data" },
                { value: "eval_alerts", label: "Run Alert Eval" },
                { value: "run_due", label: "Run Due Monitors" },
              ]}
              suffixIcon={
                toolbarAction === "reload" ? <ReloadOutlined /> : undefined
              }
            />
          </div>
        </div>
      </Card>

      {/* --- TABS & TABLES --- */}
      <Card bodyStyle={{ padding: 0 }} style={{ marginTop: 12 }}>
        <Tabs
          className="monitor-tabs"
          activeKey={tab}
          onChange={setTab}
          style={{ padding: "0 8px" }}
        >
          <Tabs.TabPane tab="Status" key="status">
            <Table<MonitorRow>
              className="monitor-table"
              dataSource={filteredMonitors}
              rowKey="id"
              size={TABLE_SIZE}
              loading={loading}
              pagination={false}
              components={monitorComps}
              columns={monitorCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Latest Status" key="latest">
            <Table<LatestRow>
              className="monitor-table"
              dataSource={latest}
              rowKey="id"
              size={TABLE_SIZE}
              pagination={false}
              components={latestComps}
              columns={latestCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Recent Failures (24h)" key="failures">
            <Table<FailureRow>
              className="monitor-table"
              dataSource={failures}
              rowKey={(r, i) => `${r.monitor_id}-${i}`}
              size={TABLE_SIZE}
              pagination={false}
              components={failureComps}
              columns={failureCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Active Alerts" key="alerts">
            <Table<AlertRow>
              className="monitor-table"
              dataSource={alerts.filter((a) => a.state === "firing")}
              rowKey="id"
              size={TABLE_SIZE}
              pagination={false}
              components={alertComps}
              columns={alertCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Alert Policies" key="policies">
            <Table<AlertPolicyRow>
              className="monitor-table"
              dataSource={policies}
              rowKey="id"
              size={TABLE_SIZE}
              pagination={false}
              components={policyComps}
              columns={policyCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Silences" key="silences">
            <Table<SilenceRow>
              className="monitor-table"
              dataSource={silences}
              rowKey="id"
              size={TABLE_SIZE}
              pagination={false}
              components={silenceComps}
              columns={silenceCols}
              scroll={getScroll()}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* --- DRAWER (SLIDER) --- */}
      <Drawer
        title={monitorEditing?.id ? "Edit Monitor" : "New Monitor"}
        open={monitorModalOpen}
        onClose={handleDrawerClose}
        placement="left" // <--- LEFT SIDE SLIDER
        // Responsive width logic: if screen < 500px use 100%, else 480px
        width={
          typeof window !== "undefined" && window.innerWidth < 500
            ? "100%"
            : 480
        }
        // Save/Cancel buttons in the header (Top Right)
        extra={
          <Space>
            <Button onClick={handleDrawerClose}>Cancel</Button>
            <Button onClick={saveMonitor} type="primary">
              Save
            </Button>
          </Space>
        }
      >
        {/* Hidden Anchor for Focus Snapping */}
        <div
          ref={topDrawerRef}
          tabIndex={-1}
          style={{ outline: "none", height: 0, overflow: "hidden" }}
          aria-hidden="true"
        />

        <Form layout="vertical" form={monitorForm}>
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "url", label: "url" },
                { value: "fqdn", label: "fqdn" },
                { value: "ip", label: "ip" },
              ]}
            />
          </Form.Item>
          <Form.Item name="target" label="Target" rules={[{ required: true }]}>
            <Input placeholder="e.g. https://google.com" />
          </Form.Item>
          <Form.Item
            name="module"
            label="Module"
            rules={[{ required: true }]}
            initialValue="http_2xx"
          >
            <Select
              options={[
                { value: "http_2xx", label: "http_2xx" },
                { value: "tcp_connect", label: "tcp_connect" },
                { value: "dns", label: "dns" },
              ]}
            />
          </Form.Item>
          <Form.Item name="period" label="Period" initialValue="00:01:00">
            <Input />
          </Form.Item>
          <Form.Item name="retention" label="Retention" initialValue="30 days">
            <Input />
          </Form.Item>
          <Form.Item
            name="enabled"
            label="Enabled"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default MonitorControl;
